import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { Provider } from 'react-redux';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress } from '@mui/material';
import theme from './theme';
import { store, persistor } from './redux/store';
import { PersistGate } from 'redux-persist/integration/react';

// Shell — keep eager for fast auth/routing
import Login from './components/auth/Login';
import Signup from './components/Signup';
import Layout from './components/layout/Layout';
import PrivateRoute from './components/routing/PrivateRoute';
import TokenExpirationHandler from './components/TokenExpirationHandler';
import InactivityLogout from './components/InactivityLogout';
import PortalPrivateRoute from './components/portal/PortalPrivateRoute';
import PortalLayout from './components/portal/PortalLayout';

// Lazy-loaded route components
const Home = lazy(() => import('./components/home/Home'));
const Bookkeeping = lazy(() => import('./components/bookkeeping/Bookkeeping'));
const CashFlow = lazy(() => import('./components/CashFlow'));
const IncomeStatement = lazy(() => import('./components/IncomeStatement'));
const BalanceSheet = lazy(() => import('./components/BalanceSheet'));
const Test = lazy(() => import('./components/home/Test'));
const InventoryManagement = lazy(() => import('./components/inventory/InventoryManagement'));
const Profile = lazy(() => import('./components/profile/Profile'));
const EmployeeManagement = lazy(() => import('./components/employees/EmployeeManagement'));
const AttendanceManagement = lazy(() => import('./components/attendance/AttendanceManagement'));
const TaskListPage = lazy(() => import('./components/tasks/TaskListPage'));
const Sales = lazy(() => import('./components/Sales'));
const Returns = lazy(() => import('./components/Returns'));
const CustomerManagement = lazy(() => import('./components/customers/CustomerManagement'));
const CarAssignmentTab = lazy(() => import('./components/cars/CarAssignmentTab'));
const CompanyExpenses = lazy(() => import('./components/expenses/CompanyExpenses'));
const TopupManagement = lazy(() => import('./components/topup/TopupManagement'));
const CustomerServiceOrder = lazy(() => import('./components/cso/CustomerServiceOrder'));
const CustomerComplaintTickets = lazy(() => import('./components/cso/CustomerComplaintTickets'));
const Jobs = lazy(() => import('./components/jobs/Jobs'));
const Survey = lazy(() => import('./components/survey/Survey'));
const PointOfSale = lazy(() => import('./components/pos/PointOfSale'));
const Categories = lazy(() => import('./components/corrections/Categories'));
const Reports = lazy(() => import('./components/reports/Reports'));
const InventoryReport = lazy(() => import('./components/reports/InventoryReport'));
const ItemWiseReport = lazy(() => import('./components/reports/ItemWiseReport'));
const ItemHistoryReport = lazy(() => import('./components/reports/ItemHistoryReport'));
const CustomerReport = lazy(() => import('./components/reports/CustomerReport'));
const JobReport = lazy(() => import('./components/reports/JobReport'));
const CSOReport = lazy(() => import('./components/reports/CSOReport'));
const CompanyExpensesReport = lazy(() => import('./components/reports/CompanyExpensesReport'));
const ActivityLog = lazy(() => import('./components/admin/ActivityLog'));
const PortalLogin = lazy(() => import('./components/portal/PortalLogin'));
const PortalSignup = lazy(() => import('./components/portal/PortalSignup'));
const PortalDashboard = lazy(() => import('./components/portal/PortalDashboard'));
const PortalNewTicket = lazy(() => import('./components/portal/PortalNewTicket'));
const PortalKnowledgeBase = lazy(() => import('./components/portal/PortalKnowledgeBase'));

const RouteFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
    <CircularProgress />
  </Box>
);

const App = () => {
  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <ThemeProvider theme={theme}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <CssBaseline />
            <Router>
              <TokenExpirationHandler />
              <InactivityLogout />
              <Suspense fallback={<RouteFallback />}>
                <Routes>

                <Route
                  path="/test"
                  element={
                    <Layout>
                      <Test />
                    </Layout>
                  }
                />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route
                  path="/customer-complaint"
                  element={
                    <Navigate
                      to={localStorage.getItem('portalToken')
                        ? '/customer-complaint/dashboard'
                        : '/customer-complaint/login'}
                      replace
                    />
                  }
                />
                <Route
                  path="/customer-complaint/"
                  element={
                    <Navigate
                      to={localStorage.getItem('portalToken')
                        ? '/customer-complaint/dashboard'
                        : '/customer-complaint/login'}
                      replace
                    />
                  }
                />
                <Route path="/customer-complaint/login" element={<PortalLogin />} />
                <Route path="/customer-complaint/signup" element={<PortalSignup />} />
                <Route
                  path="/customer-complaint/dashboard"
                  element={(
                    <PortalPrivateRoute>
                      <PortalLayout>
                        <PortalDashboard />
                      </PortalLayout>
                    </PortalPrivateRoute>
                  )}
                />
                <Route
                  path="/customer-complaint/new-ticket"
                  element={(
                    <PortalPrivateRoute>
                      <PortalLayout>
                        <PortalNewTicket />
                      </PortalLayout>
                    </PortalPrivateRoute>
                  )}
                />
                <Route
                  path="/customer-complaint/knowledge-base"
                  element={(
                    <PortalPrivateRoute>
                      <PortalLayout>
                        <PortalKnowledgeBase />
                      </PortalLayout>
                    </PortalPrivateRoute>
                  )}
                />
                <Route path="/" element={<Navigate to="/profile" />} />

                <Route
                  path="/home"
                  element={
                    <PrivateRoute requiredRoles={['COMPANY_ADMIN', 'TODO_LIST']} requireAny={true}>
                      <Layout>
                        <Home />
                      </Layout>
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/bookkeeping"
                  element={
                    <PrivateRoute requiredRoles={['BOOKKEEPING']} requireAny={true}>
                      <Layout>
                        <Bookkeeping />
                      </Layout>
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/cash-flow"
                  element={
                    <PrivateRoute requiredRoles={['FINANCE']} requireAny={true}>
                      <Layout>
                        <CashFlow />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/income-statement"
                  element={
                    <PrivateRoute requiredRoles={['FINANCE']} requireAny={true}>
                      <Layout>
                        <IncomeStatement />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/balance-sheet"
                  element={
                    <PrivateRoute requiredRoles={['FINANCE']} requireAny={true}>
                      <Layout>
                        <BalanceSheet />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/employees"
                  element={
                    <PrivateRoute requiredRoles={['EMPLOYEES']} requireAny={true}>
                      <Layout>
                        <EmployeeManagement />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/attendance"
                  element={
                    <PrivateRoute requiredRoles={['ATTENDANCE']} requireAny={true}>
                      <Layout>
                        <AttendanceManagement />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/tasks"
                  element={
                    <PrivateRoute requiredRoles={['COMPANY_ADMIN', 'TASK_LIST']} requireAny={true}>
                      <Layout>
                        <TaskListPage />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <PrivateRoute>
                      <Layout>
                        <Profile />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/inventory"
                  element={
                    <PrivateRoute requiredRoles={['INVENTORY_MANAGEMENT']} requireAny={true}>
                      <Layout>
                        <InventoryManagement />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/inventory/suppliers"
                  element={
                    <PrivateRoute requiredRoles={['INVENTORY_MANAGEMENT']} requireAny={true}>
                      <Layout>
                        <InventoryManagement />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/inventory/purchase-order"
                  element={
                    <PrivateRoute requiredRoles={['INVENTORY_MANAGEMENT']} requireAny={true}>
                      <Layout>
                        <InventoryManagement />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/inventory/purchase-invoice"
                  element={
                    <PrivateRoute requiredRoles={['INVENTORY_MANAGEMENT']} requireAny={true}>
                      <Layout>
                        <InventoryManagement />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/inventory/returns"
                  element={
                    <PrivateRoute requiredRoles={['INVENTORY_MANAGEMENT']} requireAny={true}>
                      <Layout>
                        <InventoryManagement />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/purchases/purchase-order"
                  element={
                    <PrivateRoute requiredRoles={['PURCHASES']} requireAny={true}>
                      <Layout>
                        <InventoryManagement />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/purchases/purchase-invoice"
                  element={
                    <PrivateRoute requiredRoles={['PURCHASES']} requireAny={true}>
                      <Layout>
                        <InventoryManagement />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/purchases/returns"
                  element={
                    <PrivateRoute requiredRoles={['PURCHASES']} requireAny={true}>
                      <Layout>
                        <InventoryManagement />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/purchases/suppliers"
                  element={
                    <PrivateRoute requiredRoles={['PURCHASES']} requireAny={true}>
                      <Layout>
                        <InventoryManagement />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/sales"
                  element={
                    <PrivateRoute requiredRoles={['SALES']} requireAny={true}>
                      <Layout>
                        <Sales />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/sales/estimate-quotation"
                  element={
                    <PrivateRoute requiredRoles={['SALES']} requireAny={true}>
                      <Layout>
                        <Sales />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/sales/purchase-order"
                  element={
                    <PrivateRoute requiredRoles={['SALES']} requireAny={true}>
                      <Layout>
                        <Sales />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/sales/sales-order"
                  element={
                    <PrivateRoute requiredRoles={['SALES']} requireAny={true}>
                      <Layout>
                        <Sales />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/sales/delivery-order"
                  element={
                    <PrivateRoute requiredRoles={['SALES']} requireAny={true}>
                      <Layout>
                        <Sales />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/sales/invoice"
                  element={
                    <PrivateRoute requiredRoles={['SALES']} requireAny={true}>
                      <Layout>
                        <Sales />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/sales/history"
                  element={
                    <PrivateRoute requiredRoles={['SALES']} requireAny={true}>
                      <Layout>
                        <Sales />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/pos"
                  element={
                    <PrivateRoute requiredRoles={['POS']} requireAny={true}>
                      <Layout>
                        <PointOfSale />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/returns"
                  element={
                    <PrivateRoute>
                      <Layout>
                        <Returns />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/customers"
                  element={
                    <PrivateRoute requiredRoles={['CUSTOMER_MANAGEMENT']} requireAny={true}>
                      <Layout>
                        <CustomerManagement />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/customers/manage"
                  element={
                    <PrivateRoute requiredRoles={['CUSTOMER_MANAGEMENT']} requireAny={true}>
                      <Layout>
                        <CustomerManagement />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/customers/returns"
                  element={
                    <PrivateRoute requiredRoles={['CUSTOMER_MANAGEMENT']} requireAny={true}>
                      <Layout>
                        <CustomerManagement />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/car-assignments"
                  element={
                    <PrivateRoute requiredRoles={['ADMIN', 'CAR_MANAGEMENT']} requireAny={true}>
                      <Layout>
                        <CarAssignmentTab />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/company-expenses"
                  element={
                    <PrivateRoute requiredRoles={['ADMIN', 'COMPANY_EXPENSES']} requireAny={true}>
                      <Layout>
                        <CompanyExpenses />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/topup/add"
                  element={
                    <PrivateRoute requiredRoles={['INVENTORY_MANAGEMENT']} requireAny={true}>
                      <Layout>
                        <TopupManagement />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/topup/sell"
                  element={
                    <PrivateRoute requiredRoles={['POS', 'SALES', 'INVENTORY_MANAGEMENT']} requireAny={true}>
                      <Layout>
                        <TopupManagement />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/customer-service-order"
                  element={
                    <PrivateRoute requiredRoles={['CUSTOMER_SERVICE_ORDER']} requireAny={true}>
                      <Layout>
                        <CustomerServiceOrder />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/customer-service-complaints"
                  element={
                    <PrivateRoute
                      requiredRoles={['CUSTOMER_COMPLAINTS', 'CUSTOMER_SERVICE_ORDER']}
                      requireAny={true}
                    >
                      <Layout>
                        <CustomerComplaintTickets />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/surveys"
                  element={
                    <PrivateRoute requiredRoles={['CUSTOMER_SERVICE_ORDER', 'ADMIN', 'JOBS', 'SURVEY']} requireAny={true}>
                      <Layout>
                        <Survey />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/jobs"
                  element={
                    <PrivateRoute requiredRoles={['CUSTOMER_SERVICE_ORDER', 'ADMIN', 'JOBS']} requireAny={true}>
                      <Layout>
                        <Jobs />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/invoice-preview"
                  element={
                    <PrivateRoute requiredRoles={['CUSTOMER_SERVICE_ORDER', 'ADMIN', 'JOBS', 'SALES']} requireAny={true}>
                      <Sales previewOnlyMode />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/activity-log"
                  element={
                    <PrivateRoute requiredRoles={['ADMIN']} requireAny={true}>
                      <Layout>
                        <ActivityLog />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/corrections/categories"
                  element={
                    <PrivateRoute>
                      <Layout>
                        <Categories />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/reports"
                  element={
                    <PrivateRoute requiredRoles={['REPORTS', 'INVENTORY_MANAGEMENT', 'CUSTOMER_MANAGEMENT', 'JOBS', 'CUSTOMER_SERVICE_ORDER', 'COMPANY_EXPENSES']} requireAny={true}>
                      <Layout>
                        <Reports />
                      </Layout>
                    </PrivateRoute>
                  }
                >
                  <Route index element={<Navigate to="/reports/inventory" replace />} />
                  <Route path="inventory" element={<InventoryReport />} />
                  <Route path="item-wise" element={<ItemWiseReport />} />
                  <Route path="item-history" element={<ItemHistoryReport />} />
                  <Route path="customers" element={<CustomerReport />} />
                  <Route path="jobs" element={<JobReport />} />
                  <Route path="cso" element={<CSOReport />} />
                  <Route path="expenses" element={<CompanyExpensesReport />} />
                </Route>
              </Routes>
              </Suspense>
            </Router>
          </LocalizationProvider>
        </ThemeProvider>
      </PersistGate>
    </Provider>
  );
};

export default App;
